<template>
  <div class="internal-payment-listing">
    <v-layout class="my-2">
      <v-flex md4>
        <v-text-field
          v-model.trim="search"
          v-on:click:clear="clearFilter('search')"
          clearable
          dense
          filled
          flat
          label="Search"
          hide-details
          solo
          :loading="paymentLoading"
          :disabled="paymentLoading"
          color="cyan"
          @keydown.enter="getPayments()"
          @keydown.tab="getPayments()"
        ></v-text-field>
      </v-flex>
      <v-flex md4>
        <v-tooltip top content-class="custom-top-tooltip">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="mx-2 custom-bold-button white--text"
              color="cyan"
              tile
              depressed
              :disabled="paymentLoading"
              v-on:click="getPayments()"
            >
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </template>
          <span>Search</span>
        </v-tooltip>
        <v-tooltip top content-class="custom-top-tooltip">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              v-bind="attrs"
              v-on="on"
              class="ml-2 custom-bold-button white--text"
              color="cyan"
              tile
              :disabled="paymentLoading"
              v-on:click="getPayments()"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Reload</span>
        </v-tooltip>
      </v-flex>
    </v-layout>
    <ListingTable :columnCount="8" :rowData="rowData">
      <template v-slot:thead>
        <thead>
          <tr>
            <th class="simple-table-th">Credit Note #</th>
            <th class="simple-table-th">Date</th>
            <th class="simple-table-th">Customer</th>
            <th class="simple-table-th">Customer Info</th>
            <th class="simple-table-th">Invoice</th>
            <th class="simple-table-th">Transaction ID</th>
            <th class="simple-table-th">Amount</th>
            <th class="simple-table-th">Created Time</th>
            <th class="simple-table-th text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          <!-- -->
          <template v-if="rowData.length">
            <tr
              v-for="(row, index) in rowData"
              :key="index"
              v-on:click="routeToInvoice(row)"
              :class="{ 'orange lighten-5': index % 2 }"
            >
              <td class="simple-table-td">
                <div class="d-flex align-center flex-wrap">
                  <Barcode :barcode="row.barcode"></Barcode>
                  <v-tooltip
                    top
                    v-if="row.infotech_sync_status == 1 || (row.infotech_id && row.infotech_sync_status != 3 && row.infotech_sync_status != 2)">
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip v-bind="attrs" v-on="on" x-small color="green lighten-5"
                        class="green--text text--darken-3 font-weight-600 px-2 my-1 ml-2" label>
                        <v-icon x-small left color="green darken-2">mdi-check-circle</v-icon>
                        Info-Tech Synced
                      </v-chip>
                    </template>
                    <span>
                      <template v-if="row.infotech_synced_at">
                        Last Synced: {{ formatDateTime(row.infotech_synced_at) }}
                      </template>
                      <template v-else>Last Synced</template>
                    </span>
                  </v-tooltip>
                  <v-tooltip top v-else-if="row.infotech_sync_status == 3">
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip v-bind="attrs" v-on="on" x-small color="orange lighten-5"
                        class="orange--text text--darken-3 font-weight-600 px-2 my-1 ml-2" label>
                        <v-icon x-small left color="orange darken-2"
                          class="spin-infotech-icon">mdi-sync</v-icon>
                        Under Sync
                      </v-chip>
                    </template>
                    <span>Syncing in background... Please refresh shortly</span>
                  </v-tooltip>
                  <v-tooltip top v-else-if="row.infotech_sync_status == 2">
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip v-bind="attrs" v-on="on" x-small color="red lighten-5"
                        class="red--text text--darken-3 font-weight-600 px-2 my-1 ml-2" label>
                        <v-icon x-small left color="red darken-2">mdi-alert-circle</v-icon>
                        Sync Failed
                      </v-chip>
                    </template>
                    <span>{{ row.infotech_error || 'Info-Tech sync failed' }}</span>
                  </v-tooltip>
                  <v-tooltip top v-else>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip v-bind="attrs" v-on="on" x-small color="grey lighten-4"
                        class="grey--text text--darken-2 font-weight-600 px-2 my-1 ml-2" label>
                        <v-icon x-small left color="grey darken-1">mdi-cloud-off-outline</v-icon>
                        Not Synced
                      </v-chip>
                    </template>
                    <span>Not Synced to Info-Tech</span>
                  </v-tooltip>
                </div>
              </td>
              <td class="simple-table-td">
                <p class="m-0 custom-nowrap-ellipsis" v-if="row.payment_date">
                  {{ formatDate(row.payment_date) }}
                </p>
              </td>
              <td class="simple-table-td">
                <div class="payment-listing-customer">
                  <p class="m-0 custom-nowrap-ellipsis">
                    <b>Name: </b>
                    {{ row.billing_contact_person.display_name }}
                  </p>
                  <p class="m-0 custom-nowrap-ellipsis">
                    <b><v-icon small>mdi-domain</v-icon>: </b>
                    {{ row.customer.display_name }}
                  </p>
                  <p class="m-0 custom-nowrap-ellipsis">
                    <b>Email: </b>
                    <template v-if="row?.billing_contact_person?.primary_email">
                      {{ row.billing_contact_person.primary_email }}</template
                    >
                    <template v-else>
                      <em class="text-muted"> no email</em></template
                    >
                  </p>
                  <p class="m-0 custom-nowrap-ellipsis">
                    <b>Phone: </b>
                    <template v-if="row?.billing_contact_person?.primary_phone">
                      {{ row.billing_contact_person.primary_phone }}</template
                    >
                    <template v-else>
                      <em class="text-muted"> no phone number</em></template
                    >
                  </p>
                </div>
              </td>
              <td class="simple-table-td">
                <div class="payment-listing-customer">
                  <p class="m-0 custom-nowrap-ellipsis">
                    <b><v-icon small>mdi-email</v-icon>: </b>
                    <template v-if="row?.billing_contact_person?.primary_email">
                      {{ row.billing_contact_person.primary_email }}</template
                    >
                    <template v-else>
                      <em class="text-muted"> no email</em></template
                    >
                  </p>
                  <p class="m-0 custom-nowrap-ellipsis">
                    <b><v-icon small>mdi-phone</v-icon>: </b>
                    <template v-if="row?.billing_contact_person?.primary_phone">
                      {{ row.billing_contact_person.primary_phone }}</template
                    >
                    <template v-else>
                      <em class="text-muted"> no phone number</em></template
                    >
                  </p>
                </div>
              </td>
              <td class="simple-table-td">
                <div class="payment-listing-invoice" v-on:click.prevent>
                  <p class="m-0 custom-nowrap-ellipsis">
                    <Barcode
                      route="invoice.detail"
                      :id="row.invoice.id"
                      :barcode="row.invoice.barcode"
                    ></Barcode>
                  </p>
                </div>
              </td>
              <td class="simple-table-td">
                <div class="payment-listing-mode">
                   <template v-if="row?.transaction_id">
                      {{ row.transaction_id }}</template
                    >
                    <template v-else>
                      <em class="text-muted"> no transaction id</em></template
                    >
                </div>
              </td>
              <td class="simple-table-td">
                <div class="payment-listing-amount">
                    <p class="m-0 custom-nowrap-ellipsis">
                      {{ $accountingJS.formatMoney(row.total) }}
                    </p>
                </div>
              </td>
              <td class="simple-table-td">
                <TableActivity v-if="!lodash.isEmpty(row.added_by)" :data="row">
                  <template v-slot:display_name>
                    {{ row.added_by.display_name }}
                  </template>
                  <template v-slot:date_time>
                    {{ row.created_at }}
                  </template>
                  <template v-slot:format_date_time>
                    {{ formatDateTime(row.added_at) }}
                  </template>
                </TableActivity>
              </td>
              <td class="simple-table-td text-center" v-on:click.stop>
                <v-tooltip top v-if="row && (row.infotech_sync_status == 2 || row.infotech_sync_status == 3)">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      v-bind="attrs"
                      v-on="on"
                      :loading="syncingCreditNoteId == row.id || row.infotech_sync_status == 3"
                      :disabled="syncingCreditNoteId == row.id || row.infotech_sync_status == 3"
                      :color="row.infotech_sync_status == 3 ? 'orange darken-2' : 'red darken-2'"
                      v-on:click="syncCreditNoteToInfoTech(row)"
                    >
                      <v-icon size="20" :class="{'spin-infotech-icon': row.infotech_sync_status == 3}">
                        {{ row.infotech_sync_status == 3 ? 'mdi-sync' : 'mdi-cloud-refresh' }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span v-if="row.infotech_sync_status == 3">Info-Tech sync in progress...</span>
                  <span v-else>Previous sync failed ({{ row.infotech_error }}). Click to retry</span>
                </v-tooltip>
              </td>
            </tr>
          </template>
          <template v-else>
            <tr>
              <td colspan="9">
                <p
                  class="m-0 row-not-found text-center font-weight-500 font-size-16"
                >
                  <img
                    :src="$assetURL('media/error/empty.png')"
                    class="row-not-found-image"
                  />
                  Uhh... There are no credit notes at the moment.
                </p>
              </td>
            </tr>
          </template>
        </tbody>
      </template>
    </ListingTable>
    <ListingFooter
      :dataLoading="paymentLoading"
      :showingFrom="showingFrom"
      :showingTo="showingTo"
      :totalRows="totalRows"
      :currentPage="currentPage"
      :totalPages="totalPages"
    ></ListingFooter>
  </div>
</template>

<script>
import Barcode from "@/view/pages/partials/Barcode.vue";
import CommonMixin from "@/core/plugins/common-mixin";
import ListingTable from "@/view/pages/partials/Listing-Table.vue";
import ListingFooter from "@/view/pages/partials/Listing-Footer.vue";
import { QUERY, POST } from "@/core/services/store/request.module";
/*   import CustomStatus from "@/view/pages/partials/CustomStatus.vue"; */
import TableActivity from "@/view/pages/partials/Table-Activity.vue";

export default {
  mixins: [CommonMixin],
  data() {
    return {
      syncingCreditNoteId: null,
      paymentLoading: false,
      rowData: [],
      currentPage: 1,
      status: "all",
      search: null,
      totalPages: 0,
      totalRows: 0,
      rowsOffset: 0,
    };
  },
  props: {
    type: {
      type: String,
      default: null,
    },
    typeId: {
      type: Number,
      default: 0,
    },
  },
  watch: {
    typeId() {
      this.$nextTick(() => {
        this.getPayments();
      });
    },
  },
  methods: {
    clearFilter() {
      //
    },
    getPayments() {
      const data = {
        current_page: this.currentPage,
        entity: 0,
        search: this.search,
        status: this.status,
        type: this.type,
        type_id: this.typeId,
      };

      if (this.paymentLoading) {
        return false;
      }

      this.paymentLoading = true;

      this.$store
        .dispatch(QUERY, { url: "payment-credit-notes", data: data })
        .then(({ data }) => {
          this.totalPages = Math.ceil(data.total_rows / data.per_page);
          this.currentPage = data.current_page;
          this.rowData = data.rows;
          this.totalRows = data.total_rows;
          this.rowsOffset = data.offset;
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.paymentLoading = false;
        });
    },
    routeToInvoice(row) {
      this.$nextTick(() => {
        this.$router.push(
          this.getDefaultRoute("payment-credit.detail", {
            params: {
              id: row.id,
            },
          })
        );
      });
    },
    syncCreditNoteToInfoTech(row) {
      const _this = this;
      _this.syncingCreditNoteId = row.id;
      const prevSyncStatus = row.infotech_sync_status;
      row.infotech_sync_status = 3;

      _this.$store
        .dispatch(POST, {
          url: `infotech/sync/credit-note/${row.id}`,
        })
        .then(() => {
          row.infotech_sync_status = 1;
          _this.getPayments();
          _this.$emit("credit-note-updated");
        })
        .catch((error) => {
          row.infotech_sync_status = prevSyncStatus == 1 ? 1 : 2;
          _this.logError(error);
        })
        .finally(() => {
          _this.syncingCreditNoteId = null;
        });
    },
  },
  computed: {
    showingFrom() {
      return this.lodash.isEmpty(this.rowData) === false
        ? this.rowsOffset + 1
        : 0;
    },
    showingTo() {
      return this.rowsOffset + (this.rowData ? this.rowData.length : 0);
    },
  },
  mounted() {
    this.getPayments();
  },
  components: {
    Barcode,
    TableActivity,
    /*  CustomStatus, */
    ListingFooter,
    ListingTable,
  },
};
</script>

<style scoped>
@keyframes spin-infotech-anim {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spin-infotech-icon {
  animation: spin-infotech-anim 1.5s linear infinite;
}
</style>
